import { useQuery, WatchQueryFetchPolicy } from '@apollo/client'
import gql from 'graphql-tag'
import { useBlocksFromTimestamps } from 'hooks/useBlocksFromTimestamps'
import { useEthPrices } from 'hooks/useEthPrices'
import { useTheGraphClient } from 'hooks/useTheGraphClient'
import { TokenData } from 'state/tokens/types'
import { get2DayChange, getPercentChange, useDeltaTimestamps } from 'utils/thegraph'

const TOKENS_BULK = (block: number | undefined, tokens: string[]) => {
  const tokenString = JSON.stringify(tokens.map((address) => address.toLowerCase()))
  const queryString =
    `
    query tokens {
      tokens(where: {id_in: ${tokenString}},` +
    (block ? `block: {number: ${block}} ,` : ``) +
    ` subgraphError: allow) {
        id
        symbol
        name
        derivedETH
        volumeUSD
        volume
        txCount
        totalValueLocked
        feesUSD
        totalValueLockedUSD
      }
    }
    `
  return gql(queryString)
}

interface TokenFields {
  id: string
  symbol: string
  name: string
  derivedETH: string
  volumeUSD: string
  volume: string
  feesUSD: string
  txCount: string
  totalValueLocked: string
  totalValueLockedUSD: string
}

interface TokenDataResponse {
  tokens: TokenFields[]
  bundles: {
    ethPriceUSD: string
  }[]
}

interface TokenPriceFields {
  id: string
  derivedETH: string
}

interface TokenPriceDataResponse {
  tokens: TokenPriceFields[]
}

interface TokenPriceChangeData {
  priceUSD: number
  priceUSDChange: number
}

const TOKEN_PRICES_BULK = (block: number | undefined, tokens: string[]) => {
  const tokenString = JSON.stringify(tokens.map((address) => address.toLowerCase()))
  const queryString =
    `
    query tokenPrices {
      tokens(where: {id_in: ${tokenString}},` +
    (block ? `block: {number: ${block}} ,` : ``) +
    ` subgraphError: allow) {
        id
        derivedETH
      }
    }
    `
  return gql(queryString)
}

export function useFetchedTokenPriceChanges(
  tokenAddresses: string[],
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first'
): {
  loading: boolean
  error: boolean
  data?: {
    [address: string]: TokenPriceChangeData
  }
} {
  const [t24] = useDeltaTimestamps()
  const client = useTheGraphClient()
  const { blocks, error: blockError } = useBlocksFromTimestamps([t24])
  const [block24] = blocks ?? []
  const ethPrices = useEthPrices()
  const normalizedTokenAddresses = tokenAddresses.map((address) => address.toLowerCase())
  const hasTokenAddresses = normalizedTokenAddresses.length > 0

  const { loading, error, data } = useQuery<TokenPriceDataResponse>(
    TOKEN_PRICES_BULK(undefined, normalizedTokenAddresses),
    {
      client,
      fetchPolicy,
      skip: !hasTokenAddresses,
    }
  )

  const {
    loading: loading24,
    error: error24,
    data: data24,
  } = useQuery<TokenPriceDataResponse>(TOKEN_PRICES_BULK(parseInt(block24?.number), normalizedTokenAddresses), {
    client,
    fetchPolicy,
    skip: !hasTokenAddresses,
  })

  if (!hasTokenAddresses) {
    return {
      loading: false,
      error: false,
      data: {},
    }
  }

  const anyError = Boolean(error || error24 || blockError)
  const anyLoading = Boolean(loading || loading24 || !blocks)

  if (!ethPrices) {
    return {
      loading: true,
      error: false,
    }
  }

  if (anyError || anyLoading) {
    return {
      loading: anyLoading,
      error: anyError,
    }
  }

  const parsed = data?.tokens
    ? data.tokens.reduce((accum: { [address: string]: TokenPriceFields }, tokenData) => {
        accum[tokenData.id] = tokenData
        return accum
      }, {})
    : {}
  const parsed24 = data24?.tokens
    ? data24.tokens.reduce((accum: { [address: string]: TokenPriceFields }, tokenData) => {
        accum[tokenData.id] = tokenData
        return accum
      }, {})
    : {}

  const formatted = normalizedTokenAddresses.reduce((accum: { [address: string]: TokenPriceChangeData }, address) => {
    const current = parsed[address]
    const oneDay = parsed24[address]
    const priceUSD = current ? parseFloat(current.derivedETH) * ethPrices.current : 0
    const priceUSDOneDay = oneDay ? parseFloat(oneDay.derivedETH) * ethPrices.oneDay : 0
    const priceUSDChange =
      priceUSD && priceUSDOneDay ? getPercentChange(priceUSD.toString(), priceUSDOneDay.toString()) : 0

    accum[address] = {
      priceUSD,
      priceUSDChange,
    }

    return accum
  }, {})

  return {
    loading: false,
    error: false,
    data: formatted,
  }
}

/**
 * Fetch top addresses by volume
 */
export function useFetchedTokenDatas(
  tokenAddresses: string[],
  fetchPolicy: WatchQueryFetchPolicy = 'no-cache'
): {
  loading: boolean
  error: boolean
  data?: {
    [address: string]: TokenData
  }
} {
  // get blocks from historic timestamps
  const [t24, t48] = useDeltaTimestamps()
  const client = useTheGraphClient()

  const { blocks, error: blockError } = useBlocksFromTimestamps([t24, t48])
  const [block24, block48] = blocks ?? []
  const ethPrices = useEthPrices()
  const normalizedTokenAddresses = tokenAddresses.map((address) => address.toLowerCase())
  const hasTokenAddresses = normalizedTokenAddresses.length > 0

  const { loading, error, data } = useQuery<TokenDataResponse>(TOKENS_BULK(undefined, normalizedTokenAddresses), {
    client,
    fetchPolicy,
    skip: !hasTokenAddresses,
  })

  const {
    loading: loading24,
    error: error24,
    data: data24,
  } = useQuery<TokenDataResponse>(TOKENS_BULK(parseInt(block24?.number), normalizedTokenAddresses), {
    client,
    fetchPolicy,
    skip: !hasTokenAddresses,
  })

  const {
    loading: loading48,
    error: error48,
    data: data48,
  } = useQuery<TokenDataResponse>(TOKENS_BULK(parseInt(block48?.number), normalizedTokenAddresses), {
    client,
    fetchPolicy,
    skip: !hasTokenAddresses,
  })

  if (!hasTokenAddresses) {
    return {
      loading: false,
      error: false,
      data: {},
    }
  }

  const anyError = Boolean(error || error24 || error48 || blockError)
  const anyLoading = Boolean(loading || loading24 || loading48 || !blocks)

  if (!ethPrices) {
    return {
      loading: true,
      error: false,
    }
  }

  // return early if not all data yet
  if (anyError || anyLoading) {
    return {
      loading: anyLoading,
      error: anyError,
    }
  }

  const parsed = data?.tokens
    ? data.tokens.reduce((accum: { [address: string]: TokenFields }, poolData) => {
        accum[poolData.id] = poolData
        return accum
      }, {})
    : {}
  const parsed24 = data24?.tokens
    ? data24.tokens.reduce((accum: { [address: string]: TokenFields }, poolData) => {
        accum[poolData.id] = poolData
        return accum
      }, {})
    : {}
  const parsed48 = data48?.tokens
    ? data48.tokens.reduce((accum: { [address: string]: TokenFields }, poolData) => {
        accum[poolData.id] = poolData
        return accum
      }, {})
    : {}

  // format data and calculate daily changes
  const formatted = normalizedTokenAddresses.reduce((accum: { [address: string]: TokenData }, address) => {
    const current: TokenFields | undefined = parsed[address]
    const oneDay: TokenFields | undefined = parsed24[address]
    const twoDay: TokenFields | undefined = parsed48[address]
    const [volumeUSD, volumeUSDChange] =
      current && oneDay && twoDay
        ? get2DayChange(current.volumeUSD, oneDay.volumeUSD, twoDay.volumeUSD)
        : current
        ? [parseFloat(current.volumeUSD), 0]
        : [0, 0]

    const tvlUSD = current ? parseFloat(current.totalValueLockedUSD) : 0
    const tvlUSDChange = getPercentChange(current?.totalValueLockedUSD, oneDay?.totalValueLockedUSD)
    const tvlToken = current ? parseFloat(current.totalValueLocked) : 0
    const priceUSD = current ? parseFloat(current.derivedETH) * ethPrices.current : 0
    const priceUSDOneDay = oneDay ? parseFloat(oneDay.derivedETH) * ethPrices.oneDay : 0
    const priceUSDChange =
      priceUSD && priceUSDOneDay ? getPercentChange(priceUSD.toString(), priceUSDOneDay.toString()) : 0
    const txCount =
      current && oneDay
        ? parseFloat(current.txCount) - parseFloat(oneDay.txCount)
        : current
        ? parseFloat(current.txCount)
        : 0
    const feesUSD =
      current && oneDay
        ? parseFloat(current.feesUSD) - parseFloat(oneDay.feesUSD)
        : current
        ? parseFloat(current.feesUSD)
        : 0

    accum[address] = {
      exists: !!current,
      address,
      name: current ? current.name : '',
      symbol: current ? current.symbol : '',
      volumeUSD,
      volumeUSDChange,
      txCount,
      tvlUSD,
      feesUSD,
      tvlUSDChange,
      tvlToken,
      priceUSD,
      priceUSDChange,
    }

    return accum
  }, {})

  return {
    loading: anyLoading,
    error: anyError,
    data: formatted,
  }
}
